import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import { ChevronLeft, ChevronRight, Clock, FileCheck, Shield, MapPin, Star, Trophy, CheckCircle } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

const SUPABASE_STORAGE = 'https://vltfzozjztfaeifjqpcg.supabase.co/storage/v1/object/public/review-photos/';

function relativeDate(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)?.getTime()) / 1000);
  if (diff < 60) return 'poco fa';
  if (diff < 3600) return `${Math.floor(diff / 60)} minuti fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'ieri';
  if (days < 7) return `${days} giorni fa`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 settimana fa';
  if (weeks < 5) return `${weeks} settimane fa`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 mese fa';
  if (months < 12) return `${months} mesi fa`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 anno fa' : `${years} anni fa`;
}

function StarRow({ value, size = 16 }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const filled = value >= i;
    const half = !filled && value >= i - 0.5;
    stars?.push(
      <span key={i} style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
        <svg width={size} height={size} viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0 }}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#d1d5db" />
        </svg>
        {(filled || half) && (
          <svg width={size} height={size} viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0, clipPath: half ? 'inset(0 50% 0 0)' : 'none' }}>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#fbbf24" />
          </svg>
        )}
      </span>
    );
  }
  return <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>{stars}</span>;
}

// ─── FAQ Accordion Item ───────────────────────────────────────────────────────
function FaqItem({ item, index, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: !isLast ? '1px solid #f3f4f6' : 'none' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827', paddingRight: '16px' }}>
          {item?.domanda}
        </span>
        <span style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
          backgroundColor: open ? '#104d98' : '#f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.2s',
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d={open ? 'M2 6h8' : 'M6 2v8M2 6h8'}
              stroke={open ? 'white' : '#6b7280'}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>
      <div style={{
        overflow: 'hidden',
        maxHeight: open ? '400px' : '0',
        transition: 'max-height 0.35s ease',
      }}>
        <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.7', paddingBottom: '16px' }}>
          {item?.risposta}
        </p>
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

const ProfiloPubblico = () => {
  const { slug } = useParams();

  const [profile, setProfile] = useState(null);
  const [workAreas, setWorkAreas] = useState([]);
  const [services, setServices] = useState([]);
  const [vatData, setVatData] = useState(null);
  const [docData, setDocData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [scrollY, setScrollY] = useState(0);

  // Reviews state
  const [reviewsList, setReviewsList] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [reviewsVisible, setReviewsVisible] = useState(5);
  const [lightbox, setLightbox] = useState(null);

  // Gallery lightbox (separate from review lightbox)
  const [galleryLightbox, setGalleryLightbox] = useState(null);

  const recensioniRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Parallax scroll tracking
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      try {
        await supabase?.rpc('increment_profile_view', { profile_slug: slug });

        const { data: profileData, error: profileError } = await supabase
          ?.from('professional_public_profiles')
          ?.select('*')
          ?.eq('slug', slug)
          ?.eq('is_published', true)
          ?.single();

        if (profileError || !profileData) {
          setNotFound(true);
          setIsLoading(false);
          return;
        }

        setProfile(profileData);

        const { data: workAreasData } = await supabase
          ?.from('professional_work_areas')
          ?.select('name')
          ?.eq('user_id', profileData?.user_id);

        if (workAreasData) {
          setWorkAreas(workAreasData?.map(w => w?.name)?.filter(Boolean));
        }

        const { data: servicesData } = await supabase
          ?.from('professional_professions')
          ?.select('professions(name)')
          ?.eq('user_id', profileData?.user_id);

        if (servicesData) {
          setServices(servicesData?.map(pp => pp?.professions?.name)?.filter(Boolean));
        }

        const { data: vatInfo } = await supabase
          ?.from('professional_vat_info')
          ?.select('*')
          ?.eq('user_id', profileData?.user_id)
          ?.single();
        if (vatInfo) setVatData(vatInfo);

        const { data: docInfo } = await supabase
          ?.from('professional_documents')
          ?.select('*')
          ?.eq('user_id', profileData?.user_id)
          ?.single();
        if (docInfo) setDocData(docInfo);

      } catch (err) {
        console.error('Error loading profile:', err);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) loadProfile();
  }, [slug]);

  useEffect(() => {
    if (!profile?.user_id) return;
    const loadReviews = async () => {
      try {
        const { data: list } = await supabase?.rpc(
          'get_public_reviews_for_professional',
          { p_user_id: profile?.user_id }
        );
        const { data: agg } = await supabase?.rpc(
          'get_review_aggregate_for_professional',
          { p_user_id: profile?.user_id }
        );
        if (list) setReviewsList(list);
        if (agg) setAggregate(agg);
      } catch (err) {
        console.error('Error loading reviews:', err);
      }
    };
    loadReviews();
  }, [profile?.user_id]);

  // Keyboard navigation for review lightbox
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e?.key === 'Escape') {
        setLightbox(null);
      } else if (e?.key === 'ArrowLeft') {
        setLightbox(prev => prev && prev?.currentIndex > 0
          ? { ...prev, currentIndex: prev?.currentIndex - 1 }
          : prev
        );
      } else if (e?.key === 'ArrowRight') {
        setLightbox(prev => prev && prev?.currentIndex < prev?.photos?.length - 1
          ? { ...prev, currentIndex: prev?.currentIndex + 1 }
          : prev
        );
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox]);

  // Keyboard navigation for gallery lightbox
  useEffect(() => {
    if (!galleryLightbox) return;
    const handleKey = (e) => {
      if (e?.key === 'Escape') {
        setGalleryLightbox(null);
      } else if (e?.key === 'ArrowLeft') {
        setGalleryLightbox(prev => prev && prev?.currentIndex > 0
          ? { ...prev, currentIndex: prev?.currentIndex - 1 }
          : prev
        );
      } else if (e?.key === 'ArrowRight') {
        setGalleryLightbox(prev => prev && prev?.currentIndex < prev?.photos?.length - 1
          ? { ...prev, currentIndex: prev?.currentIndex + 1 }
          : prev
        );
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [galleryLightbox]);

  // SEO meta tags and Schema.org
  useEffect(() => {
    if (!profile) return;

    document.title = profile?.seo_title || `${profile?.display_name} | Volaptic`;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head?.appendChild(meta);
    }
    meta.content = profile?.seo_description || '';

    // Canonical
    const canonicalHref = `${window.location?.origin}/p/${slug}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head?.appendChild(canonical);
    }
    canonical.href = canonicalHref;

    // og:title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle?.setAttribute('property', 'og:title');
      document.head?.appendChild(ogTitle);
    }
    ogTitle.content = document.title;

    // og:description
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc?.setAttribute('property', 'og:description');
      document.head?.appendChild(ogDesc);
    }
    ogDesc.content = profile?.seo_description || '';

    // og:url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl?.setAttribute('property', 'og:url');
      document.head?.appendChild(ogUrl);
    }
    ogUrl.content = canonicalHref;

    // og:image
    const ogImageValue = profile?.cover_photo_url || profile?.logo_url || 'https://volaptic.com/og-image.png';
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage?.setAttribute('property', 'og:image');
      document.head?.appendChild(ogImage);
    }
    ogImage.content = ogImageValue;

    // twitter:title
    let twTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twTitle) {
      twTitle = document.createElement('meta');
      twTitle.name = 'twitter:title';
      document.head?.appendChild(twTitle);
    }
    twTitle.content = document.title;

    // twitter:description
    let twDesc = document.querySelector('meta[name="twitter:description"]');
    if (!twDesc) {
      twDesc = document.createElement('meta');
      twDesc.name = 'twitter:description';
      document.head?.appendChild(twDesc);
    }
    twDesc.content = profile?.seo_description || '';

    let script = document.querySelector('#ld-json');
    if (!script) {
      script = document.createElement('script');
      script.id = 'ld-json';
      script.type = 'application/ld+json';
      document.head?.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: profile?.display_name,
      description: profile?.bio_generated,
      url: window.location?.href,
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        itemListElement: (profile?.services_list || [])?.map(s => ({
          '@type': 'Offer',
          name: s,
        })),
      },
    });

    let faqScript = document.querySelector('#ld-json-faq');
    if (!faqScript && profile?.faq_list && profile?.faq_list?.length > 0) {
      faqScript = document.createElement('script');
      faqScript.id = 'ld-json-faq';
      faqScript.type = 'application/ld+json';
      document.head?.appendChild(faqScript);
      faqScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: profile?.faq_list?.map(item => ({
          '@type': 'Question',
          name: item?.domanda,
          acceptedAnswer: { '@type': 'Answer', text: item?.risposta },
        })),
      });
    }

    return () => {
      const ldScript = document.querySelector('#ld-json');
      if (ldScript) ldScript?.remove();
      const ldFaqScript = document.querySelector('#ld-json-faq');
      if (ldFaqScript) ldFaqScript?.remove();

      // Reset canonical and og:url so navigating away doesn't leave stale values
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.href = 'https://volaptic.com/';
      const ogUrlEl = document.querySelector('meta[property="og:url"]');
      if (ogUrlEl) ogUrlEl.content = 'https://volaptic.com/';
    };
  }, [profile]);

  const handleCTA = async () => {
    // Always carry the target professional info in the URL so the new-request page can read it
    const profDisplayName = profile?.company_name || profile?.display_name || profile?.profession_text || services?.[0] || '';
    const proParam = profile?.user_id ? `?pro=${encodeURIComponent(profile?.user_id)}&proName=${encodeURIComponent(profDisplayName || '')}` : '';
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        window.location.href = `/cliente/richiesta/nuova${proParam}`;
      } else {
        window.location.href = `/cliente/richiesta/nuova${proParam}`;
      }
    } catch {
      window.location.href = `/cliente/richiesta/nuova${proParam}`;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
        <p className="text-gray-500 text-lg">Professionista non trovato</p>
        <Link to="/" className="text-blue-600 hover:underline text-sm">Torna alla home</Link>
      </div>
    );
  }

  const professionLabel = profile?.profession_text || services?.[0] || '';
  const displayName = profile?.company_name || profile?.display_name || professionLabel;
  const logoUrl = profile?.logo_url || '';
  const coverPhotoUrl = profile?.cover_photo_url || '';
  const yearsExperience = profile?.years_experience || null;
  const brandsList = profile?.brands_list || [];
  const faqList = profile?.faq_list || [];
  const zonesFirst2 = (profile?.zones_list || workAreas)?.slice(0, 2);

  // Parse gallery_items (preferred) or gallery_urls
  const galleryItems = (profile?.gallery_items || [])?.filter(item => item?.url || item?.storage_path);
  const galleryPhotos = galleryItems?.length > 0
    ? galleryItems
    : (profile?.gallery_urls || [])?.map(item =>
        typeof item === 'string' ? { url: item, caption: '' } : item
      );

  // Legacy photos for "Chi sono" inline photo (first from gallery_urls)
  const photos = (profile?.gallery_urls || [])?.map(item =>
    typeof item === 'string' ? { url: item, caption: '' } : item
  );

  // Initials for avatar
  const initials = displayName ? displayName?.slice(0, 2)?.toUpperCase() : '??';

  // Reviews derived values
  const hasReviews = aggregate?.total_reviews > 0;
  const avgRating = parseFloat(aggregate?.avg_rating || 0);
  const totalReviews = aggregate?.total_reviews || 0;

  const distKeys = [5, 4, 3, 2, 1];
  const distCounts = {
    5: aggregate?.count_5 || 0,
    4: aggregate?.count_4 || 0,
    3: aggregate?.count_3 || 0,
    2: aggregate?.count_2 || 0,
    1: aggregate?.count_1 || 0,
  };

  const visibleReviews = reviewsList?.slice(0, reviewsVisible);
  // 3 most recent reviews for right rail
  const recentReviews = reviewsList?.slice(0, 3);

  // Parallax offset
  const parallaxOffset = scrollY * 0.35;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column' }}>

      {/* ── Review photo Lightbox ── */}
      {lightbox && (() => {
        const { photos: lbPhotos, currentIndex } = lightbox;
        const currentPhoto = lbPhotos?.[currentIndex];
        const lbUrl = currentPhoto
          ? `${SUPABASE_STORAGE}${currentPhoto?.storage_path || currentPhoto}`
          : '';
        const total = lbPhotos?.length || 0;
        return (
          <div
            onClick={() => setLightbox(null)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', flexDirection: 'column' }}
          >
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={e => e?.stopPropagation()}
            >
              {currentIndex > 0 && (
                <button
                  onClick={(e) => { e?.stopPropagation(); setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex - 1 })); }}
                  style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}
                >
                  <ChevronLeft size={24} color="white" />
                </button>
              )}
              <img src={lbUrl} alt="Foto recensione" onClick={e => e?.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '12px', objectFit: 'contain' }} />
              {currentIndex < total - 1 && (
                <button
                  onClick={(e) => { e?.stopPropagation(); setLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex + 1 })); }}
                  style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}
                >
                  <ChevronRight size={24} color="white" />
                </button>
              )}
            </div>
            {total > 1 && (
              <p style={{ color: 'white', fontSize: 14, textAlign: 'center', marginTop: 12 }} onClick={e => e?.stopPropagation()}>
                {currentIndex + 1} / {total}
              </p>
            )}
            <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: '16px', right: '20px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: 'white', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
        );
      })()}

      {/* ── Gallery Lightbox ── */}
      {galleryLightbox && (() => {
        const { photos: gbPhotos, currentIndex } = galleryLightbox;
        const currentItem = gbPhotos?.[currentIndex];
        const gbUrl = currentItem?.url || currentItem?.storage_path
          ? (currentItem?.url || `${SUPABASE_STORAGE}${currentItem?.storage_path}`)
          : '';
        const total = gbPhotos?.length || 0;
        return (
          <div
            onClick={() => setGalleryLightbox(null)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', flexDirection: 'column' }}
          >
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '90vw' }}
              onClick={e => e?.stopPropagation()}
            >
              {currentIndex > 0 && (
                <button
                  onClick={(e) => { e?.stopPropagation(); setGalleryLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex - 1 })); }}
                  style={{ position: 'absolute', left: -20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}
                >
                  <ChevronLeft size={24} color="white" />
                </button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <img src={gbUrl} alt={currentItem?.caption || `Foto ${currentIndex + 1}`} style={{ maxWidth: '85vw', maxHeight: '75vh', borderRadius: '12px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                {currentItem?.caption && (
                  <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, textAlign: 'center', fontStyle: 'italic' }}>{currentItem?.caption}</p>
                )}
              </div>
              {currentIndex < total - 1 && (
                <button
                  onClick={(e) => { e?.stopPropagation(); setGalleryLightbox(prev => ({ ...prev, currentIndex: prev?.currentIndex + 1 })); }}
                  style={{ position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}
                >
                  <ChevronRight size={24} color="white" />
                </button>
              )}
            </div>
            {total > 1 && (
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 16 }} onClick={e => e?.stopPropagation()}>
                {currentIndex + 1} / {total}
              </p>
            )}
            <button onClick={() => setGalleryLightbox(null)} style={{ position: 'absolute', top: '16px', right: '20px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: 'white', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
        );
      })()}

      {/* ── Minimal Public Header ── */}
      <header style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <span style={{ fontSize: '20px', fontWeight: '700', color: '#104d98', letterSpacing: '-0.5px' }}>Volaptic</span>
        <a href="/" style={{ fontSize: '14px', color: '#104d98', fontWeight: '500', textDecoration: 'none' }}>
          Trova altri professionisti →
        </a>
      </header>

      {/* ── STEP 1: Parallax Hero ── */}
      <div
        ref={heroRef}
        style={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: isMobile ? '340px' : '420px',
        }}
      >
        {/* Background layer with parallax */}
        {coverPhotoUrl ? (
          <div style={{
            position: 'absolute',
            inset: '-60px 0',
            backgroundImage: `url(${coverPhotoUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${parallaxOffset}px)`,
            willChange: 'transform',
          }} />
        ) : (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #013776 0%, #104d98 60%, #2780b5 100%)',
          }} />
        )}
        {/* Dark gradient overlay — bottom-up as specified */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: coverPhotoUrl
            ? 'linear-gradient(to top, rgba(16,77,152,0.65) 0%, rgba(16,77,152,0.35) 100%)'
            : 'transparent',
        }} />
        {/* Additional left-to-right overlay for readability */}
        {coverPhotoUrl && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(1,55,118,0.75) 0%, rgba(16,77,152,0.45) 60%, rgba(16,77,152,0.2) 100%)',
          }} />
        )}

        {/* Hero content */}
        <div style={{ position: 'relative', zIndex: 2, paddingTop: '56px', paddingBottom: '56px', paddingLeft: '24px', paddingRight: '24px' }}>
          <div style={{ maxWidth: '896px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '200px 1fr',
              gap: '32px',
              alignItems: 'center',
            }}>
              {/* Hero Left — Logo card */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}>
                {logoUrl ? (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '16px',
                    display: 'inline-block',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
                    border: '4px solid white',
                  }}>
                    <img src={logoUrl} alt={`Logo ${displayName}`} style={{ width: '120px', height: '120px', objectFit: 'contain', borderRadius: '8px' }} />
                  </div>
                ) : (
                  <div style={{
                    background: 'linear-gradient(135deg, #32b3e5, #013776)',
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
                    border: '4px solid white',
                  }}>
                    <span style={{ color: 'white', fontSize: '36px', fontWeight: '700' }}>{initials}</span>
                  </div>
                )}
              </div>

              {/* Hero Right */}
              <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                <h1 style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: 'white', lineHeight: '1.2', marginBottom: '8px' }}>
                  {displayName}
                </h1>
                {profile?.tagline && (
                  <p style={{ fontSize: isMobile ? '16px' : '20px', color: 'rgba(255,255,255,0.9)', fontWeight: '300', fontStyle: 'italic', marginBottom: '16px' }}>
                    {profile?.tagline}
                  </p>
                )}

                {/* Profession pill + zone chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                  {professionLabel && (
                    <span style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', fontSize: '14px', borderRadius: '9999px', padding: '4px 14px', fontWeight: '500' }}>
                      {professionLabel}
                    </span>
                  )}
                  {workAreas?.slice(0, 4)?.map((area, i) => (
                    <span key={i} style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: '12px', borderRadius: '9999px', padding: '4px 12px' }}>
                      {area}
                    </span>
                  ))}
                </div>

                {/* CTA button */}
                <div style={{ marginTop: '24px' }}>
                  <button
                    onClick={handleCTA}
                    style={{ backgroundColor: '#32b3e5', color: 'white', padding: '14px 32px', borderRadius: '12px', fontSize: '16px', fontWeight: '800', boxShadow: '0 8px 32px rgba(50,179,229,0.5)', border: 'none', cursor: 'pointer' }}
                  >
                    Richiedi un preventivo gratuito
                  </button>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginTop: '8px' }}>
                    Gratuito · Nessun impegno · Risposta tipica entro 2 ore
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── STEP 2: Sticky Trust Bar ── */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        position: 'sticky',
        top: '57px',
        zIndex: 40,
        borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ maxWidth: '896px', margin: '0 auto', padding: '0 16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '28px',
            overflowX: 'auto',
            padding: '14px 0',
            scrollbarWidth: 'none',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
          }}>
            {/* ✓ Verificato */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <CheckCircle size={16} color="#16a34a" />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#15803d', whiteSpace: 'nowrap' }}>Professionista verificato</span>
            </div>

            {/* Divider */}
            <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', flexShrink: 0 }} />

            {/* 🏆 Esperienza */}
            {yearsExperience && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <Trophy size={16} color="#d97706" />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>{yearsExperience} anni di esperienza</span>
                </div>
                <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', flexShrink: 0 }} />
              </>
            )}

            {/* ⭐ Recensioni */}
            {hasReviews && totalReviews > 0 && (
              <>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => recensioniRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  <Star size={16} color="#f59e0b" fill="#f59e0b" />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>
                    {avgRating?.toFixed(1)} · {totalReviews} recension{totalReviews === 1 ? 'e' : 'i'}
                  </span>
                </div>
                <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', flexShrink: 0 }} />
              </>
            )}

            {/* 📍 Zone */}
            {zonesFirst2?.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <MapPin size={16} color="#6b7280" />
                <span style={{ fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}>
                  {zonesFirst2?.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── STEP 3: 3-Column Proof Section ── */}
      <div style={{ maxWidth: '896px', margin: '32px auto 0 auto', padding: '0 16px', width: '100%' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}>
          {[
            { icon: <Clock size={28} color="#2780b5" />, title: 'Risposta tipica entro 2 ore', desc: 'Ricevi una risposta rapida alla tua richiesta' },
            { icon: <FileCheck size={28} color="#2780b5" />, title: 'Preventivi gratuiti', desc: 'Nessun costo per ricevere un preventivo dettagliato' },
            { icon: <Shield size={28} color="#2780b5" />, title: 'Nessun impegno', desc: 'Sei libero di scegliere senza vincoli' },
          ]?.map((card, i) => (
            <div
              key={i}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                border: '1px solid #e5e7eb',
                padding: '24px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                <div style={{ backgroundColor: '#eff6ff', borderRadius: '12px', padding: '12px', display: 'inline-flex' }}>
                  {card?.icon}
                </div>
              </div>
              <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>{card?.title}</h3>
              <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>{card?.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Content Area ── */}
      <div style={{ maxWidth: '896px', margin: '0 auto', padding: '0 16px 0 16px', width: '100%' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 300px',
          gap: '24px',
          alignItems: 'start',
        }}>

          {/* LEFT COLUMN */}
          <div>
            {/* Card: Chi sono */}
            {profile?.bio_generated && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '32px', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>Chi sono</h2>
                <p style={{ color: '#374151', lineHeight: '1.75', fontSize: '16px', whiteSpace: 'pre-line' }}>
                  {profile?.bio_generated}
                </p>
              </div>
            )}

            {/* ── STEP 4: Gallery Section (masonry) ── */}
            {galleryPhotos?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '24px', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>I miei lavori</h2>
                {/* Masonry using CSS columns approach */}
                <div style={{
                  columnCount: isMobile ? 1 : (window.innerWidth < 1024 ? 2 : 3),
                  columnGap: '12px',
                }}>
                  {galleryPhotos?.map((item, i) => {
                    const imgUrl = item?.url || (item?.storage_path ? `${SUPABASE_STORAGE}${item?.storage_path}` : '');
                    if (!imgUrl) return null;
                    return (
                      <figure
                        key={i}
                        style={{
                          margin: '0 0 12px 0',
                          breakInside: 'avoid',
                          cursor: 'pointer',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                        onClick={() => setGalleryLightbox({ photos: galleryPhotos, currentIndex: i })}
                      >
                        <img
                          src={imgUrl}
                          alt={item?.caption || `Lavoro ${i + 1}`}
                          style={{
                            width: '100%',
                            display: 'block',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                          onError={e => { e.target.closest('figure').style.display = 'none'; }}
                        />
                        {item?.caption && (
                          <figcaption style={{
                            position: 'absolute', bottom: 0, left: 0, right: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                            color: 'white', fontSize: '12px', padding: '20px 10px 8px',
                            fontStyle: 'italic',
                          }}>
                            {item?.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Card: Servizi offerti */}
            {profile?.services_list?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '24px', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>Cosa posso fare per te</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {profile?.services_list?.map((service, i) => (
                    <span key={i} style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '500' }}>
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ── STEP 5: FAQ Accordion ── */}
            {faqList?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '32px', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Domande frequenti</h2>
                <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>Clicca su una domanda per espandere la risposta</p>
                {faqList?.map((item, i) => (
                  <FaqItem key={i} item={item} index={i} isLast={i === faqList?.length - 1} />
                ))}
              </div>
            )}

            {/* ─── Card: Recensioni ─────────────────────────────────────── */}
            {hasReviews && (
              <div
                ref={recensioniRef}
                id="recensioni"
                style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '32px', marginBottom: '24px' }}
              >
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '20px' }}>Recensioni</h2>

                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px', fontStyle: 'italic' }}>
                  Tutte le recensioni provengono da clienti che hanno utilizzato realmente i servizi tramite Volaptic.
                </p>

                {/* Big rating row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '48px', fontWeight: '800', color: '#111827', lineHeight: 1 }}>
                    {avgRating?.toFixed(1)}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <StarRow value={avgRating} size={24} />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>{totalReviews} recension{totalReviews === 1 ? 'e' : 'i'}</span>
                  </div>
                </div>

                {/* Distribution bars */}
                <div style={{ marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {distKeys?.map(star => {
                    const count = distCounts?.[star];
                    const pct = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
                    return (
                      <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#374151', width: '28px', textAlign: 'right', flexShrink: 0 }}>{star} ★</span>
                        <div style={{ flex: 1, height: '8px', backgroundColor: '#f3f4f6', borderRadius: '9999px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#2780b5', borderRadius: '9999px', transition: 'width 0.4s ease' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: '#6b7280', width: '32px', flexShrink: 0 }}>{pct}%</span>
                      </div>
                    );
                  })}
                </div>

                {/* Review list */}
                <div>
                  {visibleReviews?.map((review, idx) => {
                    const reviewPhotos = review?.photos || [];
                    return (
                      <div key={review?.id || idx}>
                        {idx > 0 && <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '20px', paddingTop: '20px' }} />}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '4px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                            <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                              {review?.client_display_name || 'Cliente'}
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: '#15803d', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '999px', padding: '2px 8px', marginLeft: '8px' }}>
                              ✓ Recensione verificata
                            </span>
                          </span>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                            {relativeDate(review?.created_at)}
                          </span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                          <StarRow value={review?.rating || 0} size={16} />
                        </div>
                        {review?.text && (
                          <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.65', marginBottom: '10px' }}>
                            {review?.text}
                          </p>
                        )}
                        {reviewPhotos?.length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '10px' }}>
                            {reviewPhotos?.map((photo, pi) => {
                              const url = `${SUPABASE_STORAGE}${photo?.storage_path}`;
                              return (
                                <img
                                  key={pi}
                                  src={url}
                                  alt={`Foto recensione ${pi + 1}`}
                                  onClick={() => setLightbox({ photos: reviewPhotos, currentIndex: pi })}
                                  style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer', flexShrink: 0 }}
                                  onError={e => { e.target.style.display = 'none'; }}
                                />
                              );
                            })}
                          </div>
                        )}
                        {review?.professional_response && (
                          <div style={{ backgroundColor: '#f9fafb', borderLeft: '3px solid #2780b5', borderRadius: '0 8px 8px 0', padding: '12px 16px', marginTop: '10px' }}>
                            <p style={{ fontSize: '12px', fontWeight: '600', color: '#2780b5', marginBottom: '4px' }}>Risposta del professionista</p>
                            <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '4px' }}>{review?.professional_response}</p>
                            {review?.response_at && (
                              <p style={{ fontSize: '11px', color: '#9ca3af' }}>{relativeDate(review?.response_at)}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {reviewsList?.length > reviewsVisible && (
                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <button
                      onClick={() => setReviewsVisible(reviewsList?.length)}
                      style={{ backgroundColor: 'white', border: '1px solid #2780b5', color: '#2780b5', borderRadius: '10px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Mostra tutte le recensioni ({reviewsList?.length})
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR */}
          <div style={{ position: isMobile ? 'static' : 'sticky', top: '120px' }}>

            {/* ── STEP 7: Right-rail ratings card with 3 most recent reviews ── */}
            {hasReviews && recentReviews?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '20px', marginBottom: '16px' }}>
                {/* Summary row */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', cursor: 'pointer', paddingBottom: '14px', borderBottom: '1px solid #f3f4f6' }}
                  onClick={() => recensioniRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  <span style={{ fontSize: '28px', fontWeight: '800', color: '#111827', lineHeight: 1 }}>
                    {avgRating?.toFixed(1)}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <StarRow value={avgRating} size={16} />
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>{totalReviews} recension{totalReviews === 1 ? 'e' : 'i'}</span>
                  </div>
                </div>
                {/* 3 most recent reviews */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {recentReviews?.map((review, idx) => (
                    <div key={review?.id || idx} style={{ paddingBottom: idx < recentReviews?.length - 1 ? '14px' : '0', borderBottom: idx < recentReviews?.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                          {review?.client_display_name || 'Cliente'}
                        </span>
                        <StarRow value={review?.rating || 0} size={12} />
                      </div>
                      {review?.text && (
                        <p style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {review?.text}
                        </p>
                      )}
                      <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', display: 'block' }}>
                        {relativeDate(review?.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
                {totalReviews > 3 && (
                  <button
                    onClick={() => recensioniRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    style={{ marginTop: '12px', width: '100%', background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', fontSize: '12px', color: '#2780b5', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Vedi tutte le {totalReviews} recensioni →
                  </button>
                )}
              </div>
            )}

            {/* Card 1 — CTA */}
            <div style={{ backgroundColor: '#104d98', borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
              <h3 style={{ color: 'white', fontWeight: '700', fontSize: '18px', marginBottom: '8px' }}>Hai bisogno di aiuto?</h3>
              <p style={{ color: '#bfdbfe', fontSize: '14px', marginBottom: '16px' }}>Invia una richiesta — è gratuita</p>
              <button
                onClick={handleCTA}
                style={{ width: '100%', backgroundColor: 'white', color: '#104d98', borderRadius: '12px', padding: '12px', fontWeight: '700', fontSize: '14px', border: 'none', cursor: 'pointer' }}
              >
                Richiedi preventivo gratuito
              </button>
              <p style={{ color: '#93c5fd', fontSize: '12px', marginTop: '12px' }}>Risposta tipica entro 2 ore</p>
            </div>

            {/* Card 2 — Info rapide */}
            <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '24px', marginTop: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>Informazioni</h3>
              <div>
                {yearsExperience && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Esperienza</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{yearsExperience} anni</span>
                  </div>
                )}
                {workAreas?.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>Zona</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827', textAlign: 'right', maxWidth: '160px' }}>{workAreas?.slice(0, 2)?.join(', ')}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Preventivi</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Gratuiti</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Risposta</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Entro 2 ore</span>
                </div>
              </div>
            </div>

            {/* Card 3 — Marchi */}
            {brandsList?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '24px', marginTop: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>Marchi trattati</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {brandsList?.map((brand, i) => (
                    <span key={i} style={{ backgroundColor: '#f9fafb', color: '#374151', fontSize: '12px', fontWeight: '500', borderRadius: '8px', padding: '6px 12px', border: '1px solid #e5e7eb' }}>
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Card 4 — Zone di intervento */}
            {workAreas?.length > 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '24px', marginTop: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>Zone di intervento</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {workAreas?.map((zone, i) => (
                    <span key={i} style={{ backgroundColor: '#f3f4f6', color: '#6b7280', borderRadius: '9999px', padding: '4px 12px', fontSize: '12px' }}>
                      {zone}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Full-Width CTA */}
      <div style={{ maxWidth: '896px', margin: '32px auto 80px auto', padding: '0 16px' }}>
        <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderRadius: '16px', padding: '40px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
            Hai bisogno di {professionLabel || 'un professionista'}?
          </h2>
          <p style={{ color: '#6b7280', marginTop: '8px', fontSize: '16px' }}>
            Invia una richiesta e ricevi preventivi da professionisti verificati
          </p>
          <button
            onClick={handleCTA}
            style={{ marginTop: '24px', backgroundColor: '#104d98', color: 'white', borderRadius: '12px', padding: '16px 32px', fontWeight: '700', fontSize: '18px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}
          >
            Richiedi un preventivo gratuito
          </button>
        </div>
      </div>

      {/* Minimal Footer */}
      <footer style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '24px', textAlign: 'center', marginTop: 'auto' }}>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
          © Volaptic · Trova professionisti di fiducia vicino a te
        </p>
        <p style={{ fontSize: '13px', color: '#6b7280' }}>
          <a href="/" style={{ color: '#104d98', textDecoration: 'none' }}>Home</a>
          {' · '}
          <a href="/come-funziona-clienti" style={{ color: '#104d98', textDecoration: 'none' }}>Come funziona</a>
        </p>
      </footer>

      {/* ── STEP 6: Sticky Mobile CTA Bar ── */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 60,
          backgroundColor: 'white',
          borderTop: '1px solid #e5e7eb',
          padding: '12px 16px',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
        }}>
          <button
            onClick={handleCTA}
            style={{
              width: '100%',
              backgroundColor: '#32b3e5',
              color: 'white',
              borderRadius: '12px',
              padding: '14px',
              fontWeight: '800',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(50,179,229,0.4)',
            }}
          >
            Richiedi preventivo
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfiloPubblico;
